import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import { LockOutlined } from '@ant-design/icons';

import { LayoutMainPage } from 'components/main-layout';
import RibbonRoleSecurityManageRibbon from 'pages/home/ribbon/RibbonRoleSecurityManage';
import {
  RoleList,
  RoleDescription,
  RoleUserList,
  SectionHeader,
  RolePermissionList,
} from '../../components';

import * as roleSelectors from 'pages/role/controllers/selectors';
import * as roleActions from 'pages/role/controllers/actions';

import { ABILITY_ACTION, ABILITY_SUBJECT } from 'static/Permission';

import mapToMemberRoles from 'pages/role/mappers/mapToMemberRoles';

import { useCheckPermissions } from 'hooks/useCheckPermissions';

import classnames from 'classnames';

import './styles.less';

import { useUserInfo } from 'hooks/auth/useUserInfo';

const TITLE = {
  text: 'Role & Security',
  icon: <LockOutlined />,
};

const MANAGE_ALL = [
  { action: ABILITY_ACTION.MANAGE, subject: ABILITY_SUBJECT.ALL },
];

const makeGridItemClass = (className) =>
  classnames(className, 'role-management__grid-item');

const RoleManagement = (props) => {
  const { viewType = 'role-management', refEnableAutoScroll } = props;
  const { memberId } = useParams();

  const dispatch = useDispatch();
  const [showEditPage, setShowEditPage] = useState(false);

  const { userInfo } = useUserInfo();
  const roleList = useSelector(roleSelectors.selectRoleList());
  const selectedRole = useSelector(roleSelectors.selectSelectedRole());

  const roleId = selectedRole?.id;
  const memberIdOfUser = userInfo?.member?.id;

  // bp: 4841 - member admin does not allow to view edit page of standard role
  const manageAll = useCheckPermissions(MANAGE_ALL);
  const roleListData = mapToMemberRoles(roleList?.data, manageAll);

  const isSystemRole =
    selectedRole?.isStandardRole || selectedRole?.isSuperRole;

  const fetchRoleList = ({ memberId, memberIdOfUser }) => {
    if (!memberId) {
      memberIdOfUser &&
        dispatch(roleActions.getRoleList({ memberId: memberIdOfUser }));
    } else {
      //* super admin only
      dispatch(roleActions.getRoleList({ memberId: memberId }));
    }
  };

  useEffect(() => {
    if (memberId) {
      fetchRoleList({ memberId });
    } else {
      fetchRoleList({ memberIdOfUser });
    }
  }, [memberId, memberIdOfUser]);

  useEffect(() => {
    if (roleId) {
      dispatch(roleActions.getRolePermissionList({ roleId }));
    }
  }, [roleId]);

  useEffect(() => {
    return () => {
      dispatch(roleActions.setSelectedRole(null));
    };
  }, []);

  // bp: 4841 - member admin does not allow to view edit page of standard role
  useEffect(() => {
    if (!isSystemRole) setShowEditPage(true);
    else setShowEditPage(manageAll && isSystemRole);
  }, [isSystemRole, manageAll]);

  return (
    <LayoutMainPage title={TITLE}>
      <RibbonRoleSecurityManageRibbon slot='ribbon' />

      <div slot='content' className='role-management'>
        <div className={makeGridItemClass('role-management__role-list')}>
          <RoleList
            title='role list'
            roleListData={roleListData}
            selectedRole={selectedRole}
            viewType={viewType}
            refEnableAutoScroll={refEnableAutoScroll}
          />
        </div>
        <div className={makeGridItemClass('role-management__role-des')}>
          <RoleDescription
            title='role description'
            selectedRole={selectedRole}
          />
        </div>
        <div className={makeGridItemClass('role-management__user-list')}>
          <RoleUserList
            title={<SectionHeader title='user list' showPencil />}
            selectedRole={selectedRole}
            memberId={memberId || memberIdOfUser}
          />
        </div>
        <div className={makeGridItemClass('role-management__permission')}>
          <RolePermissionList
            title='Permission list'
            selectedRole={selectedRole}
            memberIdParams={memberId}
            memberId={memberIdOfUser}
            showEditPage={showEditPage}
          />
        </div>
      </div>
    </LayoutMainPage>
  );
};

export default RoleManagement;
